import { useEffect, useState } from "react";
import { BellRing, Mail, Send, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { disablePush, enablePush, pushSupported } from "@/lib/push";

const POLICY_LABELS = {
  adapt: "Adaptar el plan (recomendado)",
  notify: "Solo avisar, sin cambiar el plan",
  off: "No hacer nada automático",
};

/** Preferencia: qué hacer si no marcas un día / entreno / comida. */
export const MissedDayPolicySection = () => {
  const [policy, setPolicy] = useState("adapt");
  useEffect(() => {
    api.get("/profile").then((r) => {
      const p = r.data?.ui_prefs?.missed_day_policy;
      if (p === "adapt" || p === "notify" || p === "off") setPolicy(p);
    }).catch(() => {});
  }, []);
  const save = async (v) => {
    setPolicy(v);
    try {
      await api.patch("/profile/ui-prefs", { missed_day_policy: v });
      toast.success("Preferencia guardada");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3" data-testid="missed-day-policy-section">
      <div className="flex items-start gap-3 rounded-2xl bg-train-soft p-4">
        <SlidersHorizontal className="mt-0.5 h-5 w-5 shrink-0 text-train-ink" />
        <div>
          <p className="text-sm font-semibold">Si no marco un entreno o una comida</p>
          <p className="mt-1 text-xs text-stone-600">
            Puedes marcar «No lo hice» en el momento. Además, al pasar el día el sistema puede cerrar lo pendiente.
          </p>
        </div>
      </div>
      <Select value={policy} onValueChange={save}>
        <SelectTrigger className="h-11 rounded-2xl" data-testid="missed-day-policy-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          {Object.entries(POLICY_LABELS).map(([k, label]) => (
            <SelectItem key={k} value={k} data-testid={`missed-day-policy-${k}`}>{label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export const RemindersSection = () => {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const canPush = pushSupported();

  useEffect(() => {
    api.get("/reminders").then((x) => setR(x.data));
    if (canPush) {
      api.get("/push/status").then((x) => setPushOn(!!x.data.subscribed)).catch(() => {});
    }
  }, [canPush]);

  if (!r) return null;

  const save = async (p) => {
    const next = { ...r, ...p };
    setR(next);
    try {
      await api.put("/reminders", { enabled: next.enabled, hour: next.hour });
      toast.success(next.enabled ? `Informe diario activado a las ${next.hour}:00` : "Informe diario desactivado");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/reminders/test");
      if (data.email_skipped) toast.success("Notificación creada (email no configurado o falló el envío)");
      else toast.success(`Informe enviado a ${data.to}`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const togglePush = async (on) => {
    setPushBusy(true);
    try {
      if (on) {
        await enablePush();
        setPushOn(true);
        toast.success("Notificaciones push activadas en este dispositivo");
      } else {
        await disablePush();
        setPushOn(false);
        toast.success("Push desactivado en este dispositivo");
      }
    } catch (e) {
      toast.error(e.message || errMsg(e));
      const { data } = await api.get("/push/status").catch(() => ({ data: { subscribed: false } }));
      setPushOn(!!data.subscribed);
    } finally { setPushBusy(false); }
  };

  const testPush = async () => {
    setPushBusy(true);
    try {
      await api.post("/notifications/test");
      toast.success("Notificación de prueba enviada (campana + push si está activo)");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setPushBusy(false); }
  };

  const tzLabel = r.timezone || "Europe/Madrid";

  return (
    <div className="space-y-4" data-testid="reminders-section">
      <div className="flex items-center justify-between gap-4 rounded-2xl bg-streak-soft p-4">
        <div className="flex items-center gap-3">
          <Mail className="h-5 w-5 text-streak-ink" />
          <div>
            <p className="text-sm font-semibold">Informe diario por email</p>
            <p className="text-xs text-stone-600">Entreno y comidas del día a {r.email}</p>
          </div>
        </div>
        <Switch checked={r.enabled} onCheckedChange={(v) => save({ enabled: v })} data-testid="reminders-enabled-switch" />
      </div>

      <div className="flex items-center justify-between gap-4 rounded-2xl bg-care-soft p-4">
        <div className="flex items-center gap-3">
          <BellRing className="h-5 w-5 text-care-ink" />
          <div>
            <p className="text-sm font-semibold">Push en este dispositivo</p>
            <p className="text-xs text-stone-600">
              {canPush
                ? "Funciona en la web y si instalas la app desde el navegador (PWA). En iOS: Añadir a pantalla de inicio (16.4+)."
                : "Este navegador no soporta Web Push."}
            </p>
          </div>
        </div>
        <Switch
          checked={pushOn}
          disabled={!canPush || pushBusy}
          onCheckedChange={togglePush}
          data-testid="push-enabled-switch"
        />
      </div>

      <div>
        <label className="text-sm font-semibold">Hora de envío ({tzLabel})</label>
        <Select value={String(r.hour)} onValueChange={(v) => save({ hour: Number(v) })}>
          <SelectTrigger className="mt-2 h-11 w-40 rounded-2xl" data-testid="reminders-hour-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Array.from({ length: 24 }, (_, h) => (
              <SelectItem key={h} value={String(h)} data-testid={`reminders-hour-${h}`}>{`${String(h).padStart(2, "0")}:00`}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={test} disabled={busy} data-testid="reminders-test-btn" className="flex items-center gap-2 rounded-full border border-stone-300 px-4 py-2.5 text-sm font-semibold transition-transform active:scale-95 disabled:opacity-60">
          <Send className="h-4 w-4" /> {busy ? "Enviando…" : "Probar informe de hoy"}
        </button>
        <button onClick={testPush} disabled={pushBusy} data-testid="push-test-btn" className="flex items-center gap-2 rounded-full border border-stone-300 px-4 py-2.5 text-sm font-semibold transition-transform active:scale-95 disabled:opacity-60">
          <BellRing className="h-4 w-4" /> {pushBusy ? "Enviando…" : "Probar notificación"}
        </button>
      </div>
      {r.last_sent && <p className="text-xs text-stone-500">Último informe: {r.last_sent}</p>}
    </div>
  );
};
